import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import CustomTable from "src/components/Table/Table";
import { useDispatch, useSelector } from "react-redux";

import {
  getRoutesListAction,
  updateRouteStatusAction,
  deleteRouteAction,
} from "src/api/routes";

import CustomBackdrop from "src/components/CustomBackdrop/CustomBackdrop";
import { showToast } from "src/customHooks/ToastEmitter";

export default function Routes() {
  const dispatch = useDispatch();

  const routesList = useSelector(
    (state: any) => state.routes?.routesList
  );

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    pageNo: 1,
    pageSize: 10,
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const fetchRoutes = async (payload: any) => {
    try {
      setLoading(true);
      await dispatch(getRoutesListAction(payload));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes(formData);
  }, []);

  const fetchData = (data: any) => {
    const payload = {
      ...formData,
      pageNo: data.pageNumber,
      pageSize: data.pageSize,
    };
    setFormData(payload);
    fetchRoutes(payload);
  };

  const handleStatusChange = async (row: any, status: any) => {
    try {
      setLoading(true);
      await dispatch(updateRouteStatusAction({ id: row.id, status }));
      showToast("Status updated", { appearance: "success" });
      fetchRoutes(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (row: any) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await dispatch(deleteRouteAction(selectedRow.id));
      showToast("Deleted successfully", { appearance: "success" });
      fetchRoutes(formData);
    } finally {
      setDeleteOpen(false);
      setSelectedRow(null);
      setLoading(false);
    }
  };

  const openMap = (lat: number, lng: number) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  // ✅ COLUMNS (UNCHANGED EXCEPT LANDMARK)
  const columns = [
    { field: "routeStop", headerName: "ROUTE STOP", width: 220 },
    { field: "address", headerName: "ADDRESS", width: 260 },
    { field: "pincode", headerName: "PINCODE", width: 140 },
    { field: "landmark", headerName: "LANDMARK", width: 220 },
    { field: "captain", headerName: "CAPTAIN", width: 200 },
    { field: "createdOn", headerName: "CREATED ON", width: 180 },
    { field: "status", headerName: "STATUS", width: 150 },
    { field: "action", headerName: "ACTION", width: 100 },
    { field: "location", headerName: "", width: 160 },
  ];

  // ✅ ACTION CONTENT (NO TableCell, NO Typography WRAPPER)
  const actionContent = (column: any, row: any) => {
    if (column === "status") {
      return (
        <FormControl size="small">
          <Select
            value={row.status}
            onChange={(e) =>
              handleStatusChange(row, e.target.value)
            }
            sx={{
                backgroundColor: row.status ? "#dcfce7" : "#fee2e2",
                color: row.status ? "green" : "red",
                height: "32px",
              }}
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column === "address") {
      return (
        <Tooltip title={row.address || ""} arrow>
          <span
            style={{
              display: "inline-block",
              maxWidth: 220,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              verticalAlign: "middle",
              cursor: "default",
            }}
          >
            {row.address || "-"}
          </span>
        </Tooltip>
      );
    }
    
    if (column === "action") {
      return (
        <Tooltip title="Delete">
          <IconButton onClick={() => handleDeleteClick(row)}>
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      );
    }

    if (column === "location") {
      return (
        <span
          style={{
            textDecoration: "underline",
            cursor: "pointer",
            color: "#1976d2",
            fontWeight: 500,
          }}
          onClick={() => openMap(row.lat, row.lng)}
        >
          Show Location
        </span>
      );
    }

    return row[column];
  };

  return (
    <>
      {loading && <CustomBackdrop open={loading} />}

      {/* ✅ FINAL ALIGNMENT FIX (THIS IS THE REAL FIX) */}
      <Box
        mt={3}
        sx={{
          pt: 1,

          "& .MuiTableCell-root": {
            textAlign: "left !important",
            display: "table-cell !important",
            verticalAlign: "middle",
          },

          "& .MuiTableCell-head": {
            textAlign: "left !important",
          },
        }}
      >
        <CustomTable
          rows={routesList?.result?.data || []}
          columns={columns}
          total={routesList?.result?.total || 0}
          action={actionContent}
          fetchData={fetchData}
          resetPagination={() => {}}
        />
      </Box>

      {/* DELETE DIALOG */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Confirmation</DialogTitle>

        <DialogContent>
          Are you sure you want to delete{" "}
          <strong>{selectedRow?.routeStop}</strong>?
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>

          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}